import { useState, useEffect } from "react";
import { STORAGE_CUENTAS } from "./FinanzasConfig";

const STORAGE_ASIENTOS = 'alqos_asientos';
const STORAGE_LINEAS = 'alqos_asientos_lineas';

function hoy() { return new Date().toISOString().slice(0, 10); }
const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString('es-AR')}`;

// Misma convención de signos que AsientosContables.js
function signoParaTipo(tipo) {
  return (tipo === 'pasivo' || tipo === 'patrimonio' || tipo === 'ingreso') ? -1 : 1;
}

const TIPO_COLOR = { activo: '#1C3FAA', pasivo: '#dc2626', patrimonio: '#16a34a' };

export default function BalanceSimplificado({ onVolver, oscuro, usuario, onNavegar }) {
  const [cuentas, setCuentas] = useState([]);
  const [asientos, setAsientos] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [fechaCorte, setFechaCorte] = useState(hoy());
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const cargar = () => {
      try { setCuentas(JSON.parse(localStorage.getItem(STORAGE_CUENTAS) || '[]')); } catch {}
      try { setAsientos(JSON.parse(localStorage.getItem(STORAGE_ASIENTOS) || '[]')); } catch {}
      try { setLineas(JSON.parse(localStorage.getItem(STORAGE_LINEAS) || '[]')); } catch {}
    };
    cargar();
    setCargando(false);
    window.addEventListener('supabase-sync-complete', cargar);
    return () => window.removeEventListener('supabase-sync-complete', cargar);
  }, []);

  // ── Saldo de cada cuenta a la fecha de corte (recalculado desde los asientos confirmados) ──
  const asientosHastaCorte = asientos.filter(a => a.estado === 'confirmado' && a.fecha && a.fecha <= fechaCorte);
  const idsAsientosValidos = new Set(asientosHastaCorte.map(a => a.id));

  const saldosPorCuenta = {};
  cuentas.forEach(c => { saldosPorCuenta[c.id] = 0; });
  lineas.filter(l => idsAsientosValidos.has(l.asientoId)).forEach(l => {
    const c = cuentas.find(cc => cc.id === l.cuentaId);
    if (!c) return;
    const debe = Number(l.debe) || 0, haber = Number(l.haber) || 0;
    const delta = signoParaTipo(c.tipo) * (debe - haber);
    saldosPorCuenta[c.id] = (saldosPorCuenta[c.id] || 0) + delta;
  });

  const cuentasActivas = cuentas.filter(c => c.activo !== false);
  const porTipo = (tipo) => cuentasActivas
    .filter(c => c.tipo === tipo && Math.abs(saldosPorCuenta[c.id] || 0) > 0.005)
    .sort((a, b) => (a.codigo || '').localeCompare(b.codigo || ''))
    .map(c => ({ ...c, saldoCorte: saldosPorCuenta[c.id] || 0 }));

  const activoItems = porTipo('activo');
  const pasivoItems = porTipo('pasivo');
  const patrimonioItems = porTipo('patrimonio');

  // Resultado del ejercicio (ingresos - egresos) al corte, se suma al Patrimonio
  let ingresosAcum = 0, egresosAcum = 0;
  cuentasActivas.forEach(c => {
    const s = saldosPorCuenta[c.id] || 0;
    if (c.tipo === 'ingreso') ingresosAcum += s;
    if (c.tipo === 'egreso') egresosAcum += s;
  });
  const resultadoEjercicio = ingresosAcum - egresosAcum;

  const totalActivo = activoItems.reduce((s, c) => s + c.saldoCorte, 0);
  const totalPasivo = pasivoItems.reduce((s, c) => s + c.saldoCorte, 0);
  const totalPatrimonioBase = patrimonioItems.reduce((s, c) => s + c.saldoCorte, 0);
  const totalPatrimonio = totalPatrimonioBase + resultadoEjercicio;
  const diferencia = totalActivo - (totalPasivo + totalPatrimonio);
  const balancea = Math.abs(diferencia) < 0.01;

  const seccion = (titulo, items, total, extraLinea) => (
    <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '18px', marginBottom: '14px' }}>
      <div style={{ fontSize: '13px', fontWeight: '700', color: TIPO_COLOR[titulo.toLowerCase()] || '#111', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '.04em' }}>{titulo}</div>
      {items.length === 0 && !extraLinea ? (
        <div style={{ fontSize: '12px', color: '#9CA3AF', padding: '8px 0' }}>Sin movimientos al corte seleccionado</div>
      ) : (
        <>
          {items.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '12px', borderBottom: '1px solid #F9FAFB' }}>
              <span style={{ color: '#374151' }}>{c.codigo} — {c.nombre}</span>
              <span style={{ fontWeight: '600' }}>{money(c.saldoCorte)}</span>
            </div>
          ))}
          {extraLinea}
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #E5E7EB' }}>
        <span style={{ fontSize: '13px', fontWeight: '700' }}>TOTAL {titulo}</span>
        <span style={{ fontSize: '14px', fontWeight: '800', color: TIPO_COLOR[titulo.toLowerCase()] || '#111' }}>{money(total)}</span>
      </div>
    </div>
  );

  const exportarPDF = () => {
    const filasHTML = (items) => items.map(c => `<div class="fr"><span class="fk">${c.codigo} — ${c.nombre}</span><span class="fv">${money(c.saldoCorte)}</span></div>`).join('');
    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"/>
<title>Balance Simplificado ${fechaCorte}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',Arial,sans-serif;color:#111;font-size:12px;padding:32px 40px;max-width:820px;margin:0 auto}
@media print{body{padding:10mm 12mm}.no-print{display:none!important}@page{margin:10mm}}
.print-btn{display:inline-block;margin-bottom:20px;padding:8px 18px;background:#1C3FAA;color:white;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.hdr{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:22px;padding-bottom:14px;border-bottom:2.5px solid #1C3FAA}
.brand-name{font-size:22px;font-weight:800;color:#1C3FAA;letter-spacing:-.5px}
.doc-num{font-size:15px;font-weight:700;color:#1C3FAA;text-align:right}
.doc-meta{font-size:11px;color:#6B7280;margin-top:4px;text-align:right}
.section{margin-bottom:16px}
.stit{font-size:11px;font-weight:700;color:#1C3FAA;text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;padding-bottom:4px;border-bottom:1px solid #E5E7EB}
.fr{display:flex;justify-content:space-between;padding:4px 0;font-size:12px;border-bottom:1px solid #F9FAFB}
.fk{color:#374151}.fv{font-weight:600}
.tot{display:flex;justify-content:space-between;margin-top:8px;padding-top:8px;border-top:2px solid #1C3FAA;font-weight:800;font-size:13px}
.chk{margin-top:20px;padding:12px 16px;border-radius:8px;font-size:12px;font-weight:700;text-align:center}
</style></head><body>
<button class="print-btn no-print" onclick="window.print()">🖸 Imprimir / Guardar PDF</button>
<div class="hdr">
  <div>
    ${(usuario?.empresa && usuario.empresa !== 'ALQOS™') ? `<div class="brand-name">${usuario.empresa}</div>` : '<div class="brand-name">Balance Simplificado</div>'}
  </div>
  <div>
    <div class="doc-num">BALANCE SIMPLIFICADO</div>
    <div class="doc-meta">Fecha de corte: ${fechaCorte}</div>
  </div>
</div>
<div class="section"><div class="stit">Activo</div>${filasHTML(activoItems)}<div class="tot"><span>TOTAL ACTIVO</span><span>${money(totalActivo)}</span></div></div>
<div class="section"><div class="stit">Pasivo</div>${filasHTML(pasivoItems)}<div class="tot"><span>TOTAL PASIVO</span><span>${money(totalPasivo)}</span></div></div>
<div class="section"><div class="stit">Patrimonio</div>${filasHTML(patrimonioItems)}<div class="fr"><span class="fk">Resultado del Ejercicio</span><span class="fv">${money(resultadoEjercicio)}</span></div><div class="tot"><span>TOTAL PATRIMONIO</span><span>${money(totalPatrimonio)}</span></div></div>
<div class="chk" style="background:${balancea ? '#F0FDF4' : '#FEF2F2'};color:${balancea ? '#16a34a' : '#dc2626'}">
  ${balancea ? '✓ Balance cuadrado: Activo = Pasivo + Patrimonio' : `✗ Balance NO cuadra — diferencia de ${money(Math.abs(diferencia))}`}
</div>
</body></html>`;
    const w = window.open('', '_blank', 'width=920,height=780');
    if (!w) { alert('Habilitá las ventanas emergentes para exportar el PDF.'); return; }
    w.document.write(html);
    w.document.close();
  };

  if (cargando) return null;

  return (
    <div style={{ padding: '24px', fontFamily: 'Inter, sans-serif', background: '#E8E9F6', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={onVolver} style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', color: '#6B7280' }}>← Menú</button>
          <span style={{ fontSize: '22px' }}>📊</span>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1C3FAA', margin: 0 }}>Balance Simplificado</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600' }}>Fecha de corte</label>
          <input type="date" value={fechaCorte} onChange={e => setFechaCorte(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '13px' }} />
          <button onClick={exportarPDF} style={{ background: '#1C3FAA', color: 'white', border: 'none', borderRadius: '10px', padding: '9px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>📄 Exportar PDF</button>
        </div>
      </div>

      {seccion('ACTIVO', activoItems, totalActivo)}
      {seccion('PASIVO', pasivoItems, totalPasivo)}
      {seccion('PATRIMONIO', patrimonioItems, totalPatrimonio, (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: '12px', borderBottom: '1px solid #F9FAFB' }}>
          <span style={{ color: '#374151' }}>Resultado del Ejercicio</span>
          <span style={{ fontWeight: '600', color: resultadoEjercicio >= 0 ? '#16a34a' : '#dc2626' }}>{money(resultadoEjercicio)}</span>
        </div>
      ))}

      <div style={{ background: balancea ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${balancea ? '#bbf7d0' : '#fecaca'}`, borderRadius: '12px', padding: '14px 18px', textAlign: 'center', fontSize: '13px', fontWeight: '700', color: balancea ? '#16a34a' : '#dc2626' }}>
        {balancea ? '✓ Balance cuadrado: Activo = Pasivo + Patrimonio' : `✗ Balance NO cuadra — diferencia de ${money(Math.abs(diferencia))}`}
      </div>
    </div>
  );
}
